import { useEffect, useState } from 'react';

const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

/**
 * Tells `BaseModal` which variant to render: the desktop dialog at or above 768px, the mobile
 * bottom sheet below it.
 *
 * It subscribes to the media query instead of listening to `resize` and storing the raw width.
 * The width changes on every pixel of a drag while the answer this hook gives changes exactly
 * once, when the breakpoint is crossed, so the old shape re-rendered the open modal and its whole
 * content hundreds of times for nothing.
 *
 * `isDesktop` starts as `false` on purpose: the server cannot know the viewport, so returning
 * anything else on the first render would make the client produce markup different from the one it
 * hydrates.
 */
export const useModalBreakPoints = () => {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => setIsDesktop(event.matches);

    setIsDesktop(mediaQuery.matches);

    // `addEventListener` on a MediaQueryList is unavailable before Safari 14, and a share of the
    // worker app runs on older phones, so keep the deprecated API as a fallback.
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return { isDesktop };
};
